import axios from "axios";
import React, { useEffect, useState, memo, useRef } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import Loader from "../../../../components/Loader";
import Table from "react-bootstrap/Table";
import { useTranslation } from "react-i18next";
import { getBankDetails } from "../../../../redux/auth/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet";
import { specialCharacterTest } from "common/Regex";
import { OTPModalUI } from "./OTPModalUI";
import { mobileScroll } from "common/FunctionUtils";
import { LOCALES } from "common/constants";

const BankInformation = ({ setShowBankInfoPopup, showBankInfoPopup, isPopup = false }) => {
  const dispatch = useDispatch();
  const { isLoading, bankDetails } = useSelector((details) => details.auth);
  // --------------------------------Translator--------------------------------------

  const { t } = useTranslation();
  const language = localStorage.getItem("i18nextLng");

  // --------------------------------UseState--------------------------------------
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hideNote, setHideNote] = useState(false);

  const inputRefs = useRef([]);
  const [OTPModal, setOTPModal] = useState(false);
  const [OTPField, setOTPField] = useState(new Array(4).fill(""));
  const [counter, setCounter] = useState(300);

  // --------------------------------UseForm--------------------------------------

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm();

  // --------------------------------Watch-UseForm--------------------------------------

  const bankName = watch("bank");

  // --------------------------------Get Bank Details--------------------------------------

  useEffect(() => {
    dispatch(getBankDetails());
  }, [dispatch]);

  useEffect(() => {
    bankDetails.forEach((data) => {
      reset({ ...data });
    });
    setData(bankDetails[0]);
  }, [bankDetails, reset]);

  // --------------------------------Edit Bank Details--------------------------------------

  const [toggle, setToggle] = useState(false);
  const [bankValues, setBankValues] = useState();

  const generateOTP = async () => {
    setLoading(true);
    try {


      const URL = "/settings/generate-otp";

      const body = {
        type: 3,
        email: "",
        lang: language,
      };

      const result = await axios.post(URL, body);
      if (result?.status === 200) {
        setLoading(false);
        toast.success(t("Email has been successfully sent to your email"));
        setOTPModal(true);
      }
    } catch (error) {
      setLoading(false);
      toast.error(t(error?.response?.data?.message));
    }
  };

  const onSubmit = async (values) => {
      if (!["Foreign bank BIC / IBAN", "Revolut Bank"].includes(values.bank)) {
      if (values.clearingNumber && isRestrictedClearingNumber(values.clearingNumber)) {
        setError("clearingNumber", {
          type: "manual",
          message: t("CLEARING_NUMBER_NOT_ALLOWED_ERROR"),
        });
        setToggle(false); // Close the confirmation modal
        return; // Don't proceed if clearing number is restricted
      }
    }
    
    setToggle(false);
    setBankValues(values);
    generateOTP();
  };

  useEffect(() => {
    if (["Foreign bank BIC / IBAN", "Revolut Bank"].includes(bankName)) {
      setHideNote(true);
    } else {
      setHideNote(false);
    }
  }, [bankName]);

  const handleClose = () => setToggle(false);

  // Function to check if clearing number is in restricted ranges
  const isRestrictedClearingNumber = (value) => {
    if (!value || value.length < 4) return false;
    
    const firstFourDigits = parseInt(value.substring(0, 4));
    
    // Check the restricted ranges: 3400-3409, 9020-9029, 9060-9069
    return (
      (firstFourDigits >= 3400 && firstFourDigits <= 3409) ||
      (firstFourDigits >= 9020 && firstFourDigits <= 9029) ||
      (firstFourDigits >= 9060 && firstFourDigits <= 9069)
    );
  };

  const handleShow = () => {
    // Get current form values
    const currentValues = watch();
    
    // Validate clearing number on submit if it's not for foreign banks
    if (!["Foreign bank BIC / IBAN", "Revolut Bank"].includes(currentValues.bank)) {
      if (currentValues.clearingNumber && isRestrictedClearingNumber(currentValues.clearingNumber)) {
        setError("clearingNumber", {
          type: "manual",
          message: t("CLEARING_NUMBER_NOT_ALLOWED_ERROR"),
        });
        return; // Don't proceed with submit
      }
    }
    
    setToggle(true);
  };

  let isValidated;
  const validateClearingNumber = (value) => {
    isValidated = specialCharacterTest(value);
    if (!isValidated) {
      setError("clearingNumber", {
        type: "manual",
        message: `${t(
          "Please do not enter hyphen and any other special character"
        )}`,
      });
    } else {
      clearErrors("clearingNumber");
    }
    return true;
  };

  const validateAccountNumber = (value) => {
    isValidated = specialCharacterTest(value);
    if (!isValidated) {
      setError("accountNumber", {
        type: "manual",
        message: `${t(
          "Please do not enter hyphen and any other special character"
        )}`,
      });
    } else {
      clearErrors("accountNumber");
    }
    return true;
  };

  const handleChangeOTP = async (index, e) => {
    const value = e.target.value;
    if (isNaN(value)) return;

    const newOtp = [...OTPField];

    newOtp[index] = value.substring(value.length - 1);
    setOTPField(newOtp);

    if (value && index < 4 - 1 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus();
    }
    if (value && index === 3) {
      const combinedOTP = newOtp.join("");

      try {
        setLoading(true);
        const URL = "/settings/save-customer-salary";
        const payload = {
          id: data?.id,
          ...bankValues,
          incomeTax: bankValues.incomeTax,
          accountNumber: bankValues.accountNumber
            ? bankValues.accountNumber
            : "",
          clearingNumber: bankValues.clearingNumber
            ? bankValues.clearingNumber
            : "",
          bank:
            bankValues.bank === "Other Bank"
              ? bankValues.OtherBank
              : bankValues.bank,
          code: combinedOTP,
        };
        const result = await axios.put(URL, payload);
        if (result?.status === 200) {
          await dispatch(getBankDetails());
          toast.success(t(result?.data?.message));
          setOTPModal(false);
          setShowBankInfoPopup(false);
          setOTPField(new Array(4).fill(""));
        } else {
          toast.error(t("Something went wrong please try again."));
        }
      } catch (err) {
        if (err?.response?.data) {
          toast.error(t(err?.response?.data?.message));
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const resendOTP = () => {
    setOTPField(new Array(4).fill(""));
    generateOTP();
  };

  useEffect(() => {
    mobileScroll();
  }, []);

  return (
    <div className={`page-content SalaryInfromation invoice-space2 info-space salry_respnv ${!showBankInfoPopup ? "mt-5" : ""}`}>
      <Helmet>
        {language === LOCALES.EN_US ? (
          <link
            rel="canonical"
            href="https://invozio.com/dashboard/settings/bank-information"
          />
        ) : (
          <link
            rel="canonical"
            href="https://invozio.com/instrumentbrada/installningar/bankinformation"
          />
        )}
      </Helmet>
      {(isLoading || loading) && <Loader />}

{ !isPopup && toggle &&(
      <Modal show={toggle} onHide={handleClose} animation={false}>
        <Modal.Header closeButton>
          <Modal.Title>{t("Bank Information")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("Are you sure you want to update your bank account information?")}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            {t("No")}
          </Button>
          <Button
            variant="primary"
            className="theme_btn"
            onClick={handleSubmit(onSubmit)}
          >
            {t("Yes")}
          </Button>
        </Modal.Footer>
      </Modal>)}

      {isPopup && toggle &&(
      <div style={{
        padding: "32px 24px",
        textAlign: "center",
        position: "relative"
      }}>
        <div style={{
          background: "#e9f5ff",
          borderRadius: "50%",
          width: 64,
          height: 64,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 16px"
        }}>
          <svg width="32" height="32" fill="#007bff" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
          </svg>
        </div>
        <h2 style={{fontWeight: 700, fontSize: 22, marginBottom: 12, color: "#222"}}>{t("Bank Information")}</h2>
        <p style={{fontSize: 16, color: "#555", marginBottom: 28}}>
          {t("Are you sure you want to update your bank account information?")}
        </p>
        <div style={{display: "flex", justifyContent: "center", gap: 16}}>
          <Button
            variant="primary"
            style={{
              minWidth: 100,
              borderRadius: 8,
              fontWeight: 500,
              border: "1px solid #ced4da"
            }}
            onClick={handleClose}
          >
            {t("No")}
          </Button>
          <Button
            variant="primary"
            className="theme_btn"
            style={{
              minWidth: 100,
              borderRadius: 8,
              fontWeight: 500,
              boxShadow: "0 2px 8px rgba(0,123,255,0.10)"
            }}
            onClick={handleSubmit(onSubmit)}
          >
            {t("Yes")}
          </Button>
        </div>
      </div>)}
     
{((!toggle && !(OTPModal === 1 || OTPModal === 2 || OTPModal === true) && isPopup) || !isPopup)  &&(
      <div>

      <div className={`${showBankInfoPopup ? "mt-3" : "settings"}`}>
        <form onSubmit={handleSubmit(handleShow)} className="bank-info">
          <div className="slary_allowance">
             {!showBankInfoPopup && (
            <h4 className="accident_ins font-weight-bold">
              {t("Salary and allowance")}
            </h4>)}
            <h3>
               {!showBankInfoPopup && (
              <span>{t("Bank information")}</span>)}
               {showBankInfoPopup ? (
              <p className="mb-0" style={{ fontSize: "14px" }}>
                {t(
                  "It looks like you forgot to enter your bank details. To complete your setup and receive payments, you can securely enter them here to receive payments and complete your setup."
                )}
              </p>) : (
                 <p className="mb-0" style={{ fontSize: "14px" }}>
                {t(
                  "We expressly disclaim any responsibility for discrepancies or inaccuracies in the bank account details furnished by the user."
                )}
              </p>
              )}
            </h3>
          </div>
          <div className="section">
            <div className="mb-3 position-relative">
              <label htmlFor="inputAddress">Bank</label>
              <select
                className="form-control styled-select select w-100"
                {...register("bank")}
              >
                <option> {t("Select a bank")} </option>
                <option value="Handelsbanken"> {t("Handelsbanken")} </option>
                <option value="Nordea social security number">
                  {" "}
                  {t("Nordea social security number")}
                </option>
                <option value="Nordea Account number">
                  {" "}
                  {t("Nordea Account number")}{" "}
                </option>
                <option value="Revolut Bank"> Revolut Bank </option>
                <option value="Swedbank"> Swedbank </option>
                <option value="SEB"> SEB </option>
                <option value="Foreign bank BIC / IBAN">
                  {" "}
                  {t("Foreign bank BIC / IBAN")}{" "}
                </option>
                <option value="Other Bank"> {t("Other Bank")} </option>
                {data?.bank !==
                  ("Handelsbanken" ||
                    "Nordea social security number" ||
                    "Nordea Account number" ||
                    "Revolut Bank" ||
                    "Swedbank" ||
                    "SEB" ||
                    "Foreign bank BIC / IBAN") && (
                  <option value={data?.bank}> {data?.bank} </option>
                )}
              </select>
            </div>

            {bankName === "Other Bank" && (
              <div className="mb-3 position-relative">
                <label
                  htmlFor="Bank-Details"
                  className="form-control-placeholder"
                >
                  <span>Bank Name</span>
                </label>
                <input
                  {...register("OtherBank")}
                  id="Bank-Details"
                  autoFocus
                  placeholder="Your Bank Name Here"
                  type="text"
                  className="form-control styled-select select w-100 mb-4"
                />
              </div>
            )}

            <div className="form-row m-0 justify-content-between flex-xs-column">
              {["Foreign bank BIC / IBAN", "Revolut Bank"].includes(bankName) ? (
                <>
                  <div className="form-group input-wrapper mb-0">
                    <input
                      autoFocus
                      type="text"
                      className={`form-control ${
                        errors.ibanNumber ? "is-invalid" : ""
                      }`}
                      id="ibanNumber"
                      {...register("ibanNumber", {
                        required: ["Foreign bank BIC / IBAN", "Revolut Bank"].includes(bankName),
                      })}
                    />
                    <label
                      className="form-control-placeholder"
                      htmlFor="ibanNumber"
                    >
                      {t("IBAN Number")}
                    </label>
                  </div>
                  <div className="form-group input-wrapper mb-0">
                    <input
                      className={`form-control border-radius-0 w-100 ${
                        errors.bicNumber ? "is-invalid" : ""
                      }`}
                      type="text"
                      id="bicNumber"
                      {...register("bicNumber", {
                        required: ["Foreign bank BIC / IBAN", "Revolut Bank"].includes(bankName),
                      })}
                    />
                    <label
                      className="form-control-placeholder"
                      htmlFor="bicNumber"
                      style={{ zIndex: "1" }}
                    >
                      {t("BIC Number")}
                    </label>
                  </div>
                </>
              ) : (
                <>
                  <div className="form-group input-wrapper mb-0">
                    <label htmlFor="inputCity"> {t("Clearing number")}</label>
                    <input
                      className={`form-control border-radius-0 w-100 ${
                        errors.clearingNumber ? "is-invalid" : ""
                      }`}
                      type="number"
                      id="clearingNumber"
                      placeholder= "e.g. 12345"
                      {...register("clearingNumber", {
                        required: !["Foreign bank BIC / IBAN", "Revolut Bank"].includes(bankName),
                        validate: validateClearingNumber,
                      })}
                      onInput={(e) => {
                        let value = e.target.value.replace(/\D/g, ""); // Remove non-numeric characters
                        if (value.length > 5) {
                          value = value.substring(0, 5); // Limit to 5 digits
                        }
                        e.target.value = value;
                        validateClearingNumber(e.target.value);
                      }}
                    />
                    {errors.clearingNumber && (
                      <div className="invalid-feedbackk">
                        {errors.clearingNumber.message}
                      </div>
                    )}
                    {/*  */}
                  </div>

                  <div className="form-group input-wrapper mb-0">
                    <label htmlFor="inputState"> {t("Account number")}</label>
                    <input
                      type="number"
                       placeholder= "e.g. 1234567890"
                      className={`form-control ${
                        errors.accountNumber ? "is-invalid" : ""
                      }`}
                      id="accountNumber"
                      {...register("accountNumber", {
                        required: !["Foreign bank BIC / IBAN", "Revolut Bank"].includes(bankName),
                        validate: validateAccountNumber,
                      })}
                      onInput={(e) => {
                        let value = e.target.value.replace(/\D/g, ""); // Remove non-numeric characters
                        if (value.length > 10) {
                          value = value.substring(0, 10); // Limit to 10 digits
                        }
                        e.target.value = value;
                        validateAccountNumber(e.target.value);
                      }}
                    />
                    {errors.accountNumber && (
                      <div className="invalid-feedbackk">
                        {errors.accountNumber.message}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            {["Foreign bank BIC / IBAN", "Revolut Bank"].includes(bankName) && (
              <p className="mb-0 note-validate-text">
                {t(
                  "Note: Clearing number should be between 4-5 digits and account number can have maximum 10 digits. Special characters are not allowed."
                )}
              </p>
            )}
          </div>
          <div style={showBankInfoPopup ? { textAlign: "end" } : {}}>
          <div className="button-wrapper mb-4">
              {showBankInfoPopup && (
           <Button
             className="text-light save_add_customer new_theme_btn mx-2"
            onClick={() => {
              setShowBankInfoPopup(false);
              onClose();
            }}
          >
            {t("Close")}
          </Button>
            )}
            <Button
              disabled={errors.clearingNumber || errors.accountNumber}
              type="submit"
              className="text-light save_add_customer new_theme_btn"
            >
              <span>{t("Save Settings")}</span>
            </Button>
          </div>
          </div>
           {/* <div style={showBankInfoPopup ? { textAlign: "end" } : {}}> */}
             {!showBankInfoPopup && (
               <div className="insurance-group">
              <h2 className="pb-0" style={{ fontSize: "15px", fontWeight: "700", fontFamily: "CircularStd-Medium", marginBottom: "0px" }}>
                {t("Get paid within one hour with Revolut.")}
              </h2>
              <p className="first" style={{fontSize: "14px", fontWeight: "400", fontFamily: "CircularStd-Book", marginTop: "0px"}}>
               <span>
                {t("If you already have a Revolut account, simply update your bank details in the platform, and we’ll pay your salary to your Revolut account. Don’t have one?")}</span> <span>  <a
                  href="https://revolut.com/referral/?referral-code=kevindtxd!JUN1-25-AR-H2&geo-redirect"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{textDecoration: "underline"}}
                > {t("Sign up here")} </a></span>  <span>{t("and start benefiting today.")}</span>
              </p>
            </div>
             )}
           {/* </div> */}
        </form>
      </div>
      {!showBankInfoPopup && (
      <div className="data-table">
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>{t("Bank Name")}</th>
              <th>
                {["Foreign bank BIC / IBAN", "Revolut Bank"].includes(bankName) && data?.ibanNumber
                  ? t("IBAN Number")
                  : t("Clearing number")}
              </th>
              <th>
                {["Foreign bank BIC / IBAN", "Revolut Bank"].includes(bankName) && data?.bicNumber
                  ? t("BIC Number")
                  : t("Account number")}
              </th>
              <th>{t("Income Tax")}</th>
              <th>{t("Created Date")}</th>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td>{t(data?.bank)}</td>
              <td>
                {["Foreign bank BIC / IBAN", "Revolut Bank"].includes(bankName) && data?.ibanNumber
                  ? data?.ibanNumber
                  : data?.clearingNumber}
              </td>
              <td>
                {["Foreign bank BIC / IBAN", "Revolut Bank"].includes(bankName) && data?.bicNumber
                  ? data?.bicNumber
                  : data?.accountNumber}
              </td>
              <td>
                {data?.incomeTax}
                {data?.incomeTax ? "%" : ""}
              </td>
              <td>{data?.createdDate?.slice(0, 10)}</td>
            </tr>
          </tbody>
        </Table>
      </div>)}
       </div>)}
        <OTPModalUI
        OTPModal={OTPModal}
        setOTPModal={setOTPModal}
        resendOTP={resendOTP}
        OTPField={OTPField}
        setOTPField={setOTPField}
        inputRefs={inputRefs}
        handleChangeOTP={handleChangeOTP}
        counter={counter}
        setCounter={setCounter}
        openInModal={!isPopup}
      />
         </div>
   
  );
};

export default memo(BankInformation);
